import { FC, MouseEvent } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { DailyNavNewsItem } from 'api';
import { shortDate } from 'utilities';
import { NewsCellProps } from './NewsCell.props';

// Strictly after the valuation date, the same rule closed transactions use.
export const isNewsSinceLastValuation = (
  publishedAt: string,
  lastValuation: string | null | undefined
): boolean => {
  if (!lastValuation) {
    return false;
  }
  return publishedAt.slice(0, 10) > lastValuation;
};

export const formatNewsSummary = (item: DailyNavNewsItem): string => {
  const date = shortDate(item.published_at) ?? item.published_at.slice(0, 10);
  return `${item.title} (${date})`;
};

const NewsCell: FC<NewsCellProps> = ({ row, onNewsClick }) => {
  const items = row.record.secondary_breakdown?.news ?? [];
  const latest = items[0];
  if (!latest) {
    return null;
  }

  const moreCount = items.length - 1;
  const sinceLastValuation = isNewsSinceLastValuation(
    latest.published_at,
    row.record.secondary_breakdown?.dates.last_valuation
  );
  const summary = formatNewsSummary(latest);

  const openDialog = (event: MouseEvent) => {
    event.stopPropagation();
    onNewsClick(row);
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      height="100%"
      pl={0.5}
      pr={0}
      sx={{ overflow: 'hidden' }}>
      <ButtonBase
        id={`news-cell-${row.companyId}`}
        data-testid={`news-cell-${row.companyId}`}
        onClick={openDialog}
        aria-label={sinceLastValuation ? `${summary}, since last valuation` : summary}
        data-since-last-valuation={sinceLastValuation ? 'true' : undefined}
        sx={{
          alignItems: 'center',
          color: sinceLastValuation ? 'error.main' : 'inherit',
          cursor: 'pointer',
          display: 'flex',
          flex: 1,
          fontFamily: 'inherit',
          fontSize: 'inherit',
          fontWeight: sinceLastValuation ? 700 : 400,
          gap: 0.75,
          justifyContent: 'flex-start',
          minWidth: 0,
          padding: 0,
          textAlign: 'left',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}>
        <Box
          component="span"
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {summary}
        </Box>
        {moreCount >= 1 && (
          <Box
            component="span"
            data-testid={`news-more-${row.companyId}`}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            {`+${moreCount} more`}
          </Box>
        )}
      </ButtonBase>
    </Box>
  );
};

export default NewsCell;
